import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { ExperiencePanel, type PanelState, type ProductInfo } from "./ExperiencePanel";

const rifleViews = [
  {
    id: "carbon",
    label: "Carbon",
    index: "01",
    image: "/media/r1-hero-pair.webp",
    alt: "Two VRTS R1 forged rifle configurations in a carbon finish",
    detail: "Forged R1 system",
  },
  {
    id: "field-earth",
    label: "Field Earth",
    index: "02",
    image: "/media/r1-field-earth.webp",
    alt: "Two VRTS R1 forged rifle configurations in Field Earth",
    detail: "Founder finish study",
  },
  {
    id: "suppressed",
    label: "Suppressed",
    index: "03",
    image: "/media/r1-suppressed.webp",
    alt: "Two VRTS R1 forged rifle configurations with suppressors",
    detail: "Integrated suppression",
  },
];

const components: ProductInfo[] = [
  {
    id: "grip-v2",
    name: "Grip V2",
    image: "/media/grip-v2.webp",
    alt: "VRTS Grip V2 in black",
    detail: "Precision-printed polymer",
    description: "A shaped polymer grip with deliberate indexing, palm support, and a consistent contact surface.",
    status: "IN PRODUCTION",
  },
  {
    id: "rail-covers",
    name: "Rail Covers",
    image: "/media/rail-covers.webp",
    alt: "VRTS rail cover texture and mounting detail",
    detail: "Four lengths with integrated indexing",
    description: "Textured rail covers provide repeatable hand placement across four lengths and a unified mounting profile.",
    status: "IN PRODUCTION",
  },
  {
    id: "barricade-stop",
    name: "Barricade Stop",
    image: "/media/barricade-stop.webp",
    alt: "VRTS barricade stop in black",
    detail: "Purpose-built control at the rail",
    description: "A compact rail-mounted control surface for deliberate placement and stable positional work.",
    status: "IN PRODUCTION",
  },
];

const suppressorProduct: ProductInfo = {
  id: "vx-556-suppressor",
  name: "5.56 Titanium Suppressor",
  image: "/media/vx-suppressor.webp",
  alt: "VRTS titanium suppressor",
  status: "IN PRODUCTION",
  detail: "Standalone suppressor and R1 system option",
  description: "A titanium suppressor developed as part of the VRTS rifle system and offered as an individual product.",
};

const navigation = [
  ["R1", "#r1"],
  ["Suppressors", "#suppressors"],
  ["Components", "#components"],
  ["Ownership", "#ownership"],
  ["Circle", "#circle"],
];

function Arrow({ diagonal = false }: { diagonal?: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className={diagonal ? "arrow arrow-diagonal" : "arrow"}>
      <path d="M3 10h13M11 5l5 5-5 5" />
    </svg>
  );
}

function Mark() {
  return (
    <span className="brand" aria-label="VRTS">
      <span className="brand-v">V</span>RTS
    </span>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState(rifleViews[0]);
  const [panel, setPanel] = useState<PanelState>(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const heroImageY = useTransform(scrollYProgress, [0, 0.28], [0, reduceMotion ? 0 : 90]);
  const heroCopyY = useTransform(scrollYProgress, [0, 0.22], [0, reduceMotion ? 0 : -42]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0.25]);

  useEffect(() => {
    document.body.classList.toggle("menu-open", menuOpen);
    return () => document.body.classList.remove("menu-open");
  }, [menuOpen]);

  useEffect(() => {
    const targetId = decodeURIComponent(window.location.hash.slice(1));
    if (!targetId) return;
    const frame = requestAnimationFrame(() => document.getElementById(targetId)?.scrollIntoView());
    return () => cancelAnimationFrame(frame);
  }, []);

  const closeMenu = () => setMenuOpen(false);
  const closePanel = useCallback(() => setPanel(null), []);
  const openInterest = useCallback((source: string, build?: string) => {
    setPanel({ kind: "interest", source, build });
  }, []);

  return (
    <div className="site-shell" id="top">
      <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} />

      <header className="site-header">
        <a className="brand-link" href="#top" onClick={closeMenu}>
          <Mark />
          <span className="brand-region">USA</span>
        </a>

        <nav className={menuOpen ? "desktop-nav nav-open" : "desktop-nav"} aria-label="Primary navigation">
          {navigation.map(([label, href]) => (
            <a key={label} href={href} onClick={closeMenu}>
              {label}
            </a>
          ))}
        </nav>

        <button className="header-cta header-button" type="button" onClick={() => setPanel({ kind: "configure" })}>
          Configure R1 <Arrow />
        </button>

        <button
          className="menu-toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
        </button>
      </header>

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <motion.div className="hero-media" style={{ y: heroImageY, opacity: heroOpacity }}>
            <img src="/media/r1-hero-pair.webp" alt="Two VRTS R1 forged rifle configurations" fetchPriority="high" />
          </motion.div>
          <div className="hero-scrim" />
          <motion.div
            className="hero-copy"
            style={{ y: heroCopyY }}
            initial={reduceMotion ? false : { opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="eyebrow">VRTS / SERIES 01</p>
            <h1 id="hero-title">THE FORGED FIFTY.</h1>
            <p className="hero-summary">Fifty numbered R1 rifle systems establish the first VRTS production run.</p>
            <div className="hero-actions">
              <button className="button button-primary" type="button" onClick={() => setPanel({ kind: "configure" })}>
                Configure R1 <Arrow />
              </button>
              <a className="text-link" href="#founders">
                View Founder Series <Arrow />
              </a>
            </div>
          </motion.div>

          <div className="hero-index" aria-label="R1 Founder Series details">
            <span>R1</span>
            <span>FORGED RECEIVER</span>
            <span>01 / 50</span>
          </div>
          <a className="scroll-cue" href="#r1" aria-label="Explore the R1 system">
            <span>Explore</span>
            <span className="scroll-line" />
          </a>
        </section>

        <section className="manifesto" id="r1" aria-labelledby="manifesto-title">
          <div className="section-index">
            <span>01</span>
            <span>R1 SYSTEM</span>
          </div>
          <motion.div
            className="manifesto-copy"
            initial={reduceMotion ? false : { opacity: 0, y: 44 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.8 }}
          >
            <h2 id="manifesto-title">Built as one system.</h2>
            <p>Receiver, controls, handguard, and suppression are developed around a single operating experience.</p>
          </motion.div>
        </section>

        <section className="configuration-stage" id="configure" aria-labelledby="configuration-title">
          <div className="configuration-visual">
            <AnimatePresence mode="wait">
              <motion.img
                key={activeView.id}
                src={activeView.image}
                alt={activeView.alt}
                initial={reduceMotion ? false : { opacity: 0, scale: 1.025 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              />
            </AnimatePresence>
            <span className="configuration-watermark" aria-hidden="true">R1</span>
            <div className="configuration-caption">
              <span>{activeView.label}</span>
              <span>{activeView.detail}</span>
            </div>
          </div>

          <div className="configuration-panel">
            <p className="eyebrow">CONFIGURATION STUDY</p>
            <h2 id="configuration-title">See the system take shape.</h2>
            <p>Select a finish study, then move into the full R1 builder.</p>
            <div className="view-selector" aria-label="Select an R1 presentation">
              {rifleViews.map((view) => (
                <button
                  type="button"
                  key={view.id}
                  className={activeView.id === view.id ? "view-option active" : "view-option"}
                  aria-pressed={activeView.id === view.id}
                  onClick={() => setActiveView(view)}
                >
                  <span>{view.index}</span>
                  <strong>{view.label}</strong>
                  <Arrow />
                </button>
              ))}
            </div>
            <button className="button button-light" type="button" onClick={() => setPanel({ kind: "configure" })}>
              Begin R1 Configuration <Arrow />
            </button>
          </div>
        </section>

        <section className="suppressor-section" id="suppressors" aria-labelledby="suppressor-title">
          <motion.div
            className="suppressor-copy"
            initial={reduceMotion ? false : { opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
          >
            <div className="section-index light-index">
              <span>02</span>
              <span>TITANIUM SUPPRESSION</span>
            </div>
            <p className="eyebrow">VX / 5.56 FAMILY</p>
            <h2 id="suppressor-title">Born suppressed.</h2>
            <p>Titanium suppressors are central to the VRTS system and available as standalone products.</p>
            <button className="text-link light-link inline-button" type="button" onClick={() => setPanel({ kind: "product", product: suppressorProduct })}>
              Explore Suppressors <Arrow />
            </button>
          </motion.div>
          <motion.div
            className="suppressor-media"
            initial={reduceMotion ? false : { opacity: 0, scale: 0.92, rotate: -2 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <img src="/media/vx-suppressor.webp" alt="VRTS titanium suppressor" loading="lazy" />
          </motion.div>
        </section>

        <section className="components-section" id="components" aria-labelledby="components-title">
          <div className="components-heading">
            <div className="section-index">
              <span>03</span>
              <span>COMPONENTS</span>
            </div>
            <div>
              <h2 id="components-title">The control surface.</h2>
              <p>Individually available components shaped around feel, placement, and repeatable use.</p>
            </div>
          </div>

          <div className="component-list">
            {components.map((component, index) => (
              <motion.article
                className="component-row"
                key={component.name}
                initial={reduceMotion ? false : { opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ delay: index * 0.08 }}
              >
                <span className="component-number">0{index + 1}</span>
                <div className="component-image">
                  <img src={component.image} alt={component.alt} loading="lazy" />
                </div>
                <div className="component-name">
                  <p className="eyebrow">{component.status}</p>
                  <h3>{component.name}</h3>
                  <span>{component.detail}</span>
                </div>
                <button className="component-action" type="button" onClick={() => setPanel({ kind: "product", product: component })} aria-label={`Explore ${component.name}`}>
                  <Arrow diagonal />
                </button>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="origin-section" id="ownership" aria-labelledby="origin-title">
          <div className="origin-media">
            <img src="/media/r1-receiver-detail.webp" alt="VRTS serialized R1 receiver detail" loading="lazy" />
          </div>
          <motion.div
            className="origin-copy"
            initial={reduceMotion ? false : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
          >
            <p className="eyebrow">PROOF OF ORIGIN</p>
            <h2 id="origin-title">Every build carries its record.</h2>
            <p>A serialized record connects each Founder rifle to its build, inspection, and ownership history.</p>
            <button className="text-link light-link inline-button" type="button" onClick={() => setPanel({ kind: "ownership" })}>
              Explore Ownership <Arrow />
            </button>
          </motion.div>
          <div className="origin-serial" aria-hidden="true">
            <span>VRTS</span>
            <span>R1 / 00001</span>
          </div>
        </section>

        <section className="circle-section" id="circle" aria-labelledby="circle-title">
          <div className="circle-orbit" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div className="circle-copy">
            <p className="eyebrow">VRTS CIRCLE</p>
            <h2 id="circle-title">Ownership continues.</h2>
            <p>Owner access brings product records, early drops, compatible releases, and direct support into one place.</p>
            <button className="text-link inline-button" type="button" onClick={() => openInterest("circle")}>
              Enter the Circle <Arrow />
            </button>
          </div>
        </section>

        <section className="founders-section" id="founders" aria-labelledby="founders-title">
          <div className="founders-background" aria-hidden="true">50</div>
          <div className="section-index light-index">
            <span>04</span>
            <span>FOUNDER SERIES</span>
          </div>
          <div className="founders-copy">
            <p className="eyebrow">NUMBERED 01 THROUGH 50</p>
            <h2 id="founders-title">The first fifty begin here.</h2>
            <p>Request Founder access and receive the release brief when allocation opens.</p>
            <div className="founders-actions">
              <button className="button button-primary" type="button" onClick={() => openInterest("founder")}>
                Request Founder Access <Arrow />
              </button>
              <button className="text-link light-link inline-button" type="button" onClick={() => setPanel({ kind: "configure" })}>
                Configure R1 <Arrow />
              </button>
            </div>
          </div>
          <div className="founders-mark">
            <Mark />
            <span>FOUNDER RUN / 2026</span>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div>
          <Mark />
          <span>Precision rifle systems and titanium suppression.</span>
        </div>
        <nav aria-label="Footer navigation">
          <a href="#r1">R1</a>
          <a href="#suppressors">Suppressors</a>
          <a href="#components">Components</a>
          <a href="#ownership">Ownership</a>
        </nav>
        <div className="footer-legal">
          <span>VRTS USA</span>
          <span>ALL RIGHTS RESERVED</span>
        </div>
      </footer>
      <ExperiencePanel panel={panel} onClose={closePanel} onRequestInterest={openInterest} />
    </div>
  );
}

export default App;
